import React from 'react';
import {
    Alert,
    Badge,
    Group,
    Loader,
    Progress,
    Text,
    ThemeIcon,
    Tooltip,
    UnstyledButton,
} from '@mantine/core';
import {
    IconCheck,
    IconClock,
    IconEye,
    IconEyeOff,
    IconInfoCircle,
} from '@tabler/icons-react';
import { DataTableColumn } from 'mantine-datatable';
import dayjs from 'dayjs';
import { STATUS_COLORS } from './constants';
import {
    getProgressColor,
    isSurveyCancellable,
    isSurveyStoreManagerEditingLocked,
} from './helpers';
import { SurveyRow, SurveyItemRow, SurveyPriceHandlers } from './types';
import type { CompetitorDrawerData } from './types';
import { PriceSurveyCompetitor, Store } from '../../codegen/graphql';
import formatCurrency from '../../utility/formatCurrency';
import {
    shouldShowCashbuildPriceInput,
    shouldShowPnlPriceInput,
} from '../../helpers/storeHelpers';
import { CompetitorPriceCell } from './CompetitorPriceCell';
import { PriceVarianceCell } from '../../components/survey/PriceVarianceCell';
import { CompetitorColumnHeader } from './CompetitorColumnHeader';
import { SurveyActionButton } from './SurveyActionButton';
import { SurveyZoneStoreCount } from '../../components/survey/SurveyZoneStoreCount';
import { SurveyTypeLabelCell } from '../../components/survey/SurveyTypeLabelCell';
import { ProposedSellingCellWrapper } from '../../components/survey/ProposedSellingCellWrapper';
import { ProposedSellingColumnHeader } from './ProposedSellingColumnHeader';
import { DueDateCell } from './DueDateCell';
import { SurveyMarginTableCell } from './SurveyMarginTableCell';
import { PropagationStopper } from '../../components/common';
import { SurveyScopeHeader } from './filter-columns/SurveyScopeHeader';
import { SurveyTypeHeader } from './filter-columns/SurveyTypeHeader';
import { SurveyStatusHeader } from './filter-columns/SurveyStatusHeader';
import { SurveyTableColumnFilters } from './surveyTableFilters';

const getActionsColumn = (
    isStoreManager: boolean,
    isDivisionalManager: boolean,
    onEyeClick?: (surveyId: string) => void,
    onCancelSurvey?: (surveyId: string) => void,
): DataTableColumn<SurveyRow> => ({
    accessor: 'actions',
    title: '',
    width: onCancelSurvey ? 120 : 80,
    textAlign: 'center',
    render: row => {
        const status = row.backendStatus;
        const isLocked = isSurveyStoreManagerEditingLocked({ status });
        const showEditIcon =
            (isStoreManager && !isLocked) ||
            (isDivisionalManager && status === 'DivisionalManagerReview');
        const showCancelAction =
            Boolean(onCancelSurvey) &&
            isSurveyCancellable({ status }) &&
            row.status !== 'Cancelled';
        return (
            <SurveyActionButton
                surveyId={row.id}
                showEditIcon={showEditIcon}
                onEyeClick={onEyeClick}
                onCancelSurvey={showCancelAction ? onCancelSurvey : undefined}
            />
        );
    },
});

const getSurveyListColumn = (
    columnFilters: SurveyTableColumnFilters,
): DataTableColumn<SurveyRow> => ({
    accessor: 'surveyList',
    title: (
        <SurveyTypeHeader
            currentValue={columnFilters.surveyTypeFilter}
            onChange={columnFilters.onSurveyTypeFilterChange}
        />
    ),
    width: 180,
    textAlign: 'left',
    render: ({ surveyType, label }) => (
        <SurveyTypeLabelCell surveyType={surveyType} label={label} />
    ),
});

const getAdZoneColumn = (
    columnFilters: SurveyTableColumnFilters,
): DataTableColumn<SurveyRow> => {
    let title: React.ReactNode = 'Adzone/StoreNo';
    if (columnFilters.showScopeFilter) {
        title = (
            <SurveyScopeHeader
                currentValue={columnFilters.scopeFilter}
                onChange={columnFilters.onScopeFilterChange}
                filterOptions={columnFilters.scopeFilterOptions}
                loading={columnFilters.scopeFilterLoading}
            />
        );
    }

    return {
        accessor: 'adZone',
        title,
        width: 180,
        textAlign: 'left',
        render: ({ adZone }) => <Text size="sm">{adZone}</Text>,
    };
};

const getStatusColumn = (
    columnFilters: SurveyTableColumnFilters,
    width: number,
    fallbackColor: string,
    inProgressClassName?: boolean,
): DataTableColumn<SurveyRow> => ({
    accessor: 'status',
    title: (
        <SurveyStatusHeader
            currentValue={columnFilters.statusFilter}
            onChange={columnFilters.onStatusFilterChange}
            filterOptions={columnFilters.statusFilterOptions}
        />
    ),
    width,
    textAlign: 'left',
    render: ({ status }) => {
        const color = STATUS_COLORS[status] || fallbackColor;
        const isInProgress =
            Boolean(inProgressClassName) && status === 'In Progress';
        return (
            <Badge
                color={color}
                variant="light"
                radius="xl"
                {...(isInProgress && {
                    className: '!bg-[#EAF2FF] !text-[#1458EC] !font-semibold',
                })}
            >
                {status}
            </Badge>
        );
    },
});

export const getActiveSurveysColumns = (
    isStoreManager: boolean,
    isDivisionalManager: boolean,
    columnFilters: SurveyTableColumnFilters,
    onCancelSurvey?: (surveyId: string) => void,
): DataTableColumn<SurveyRow>[] => [
    getAdZoneColumn(columnFilters),
    getSurveyListColumn(columnFilters),
    {
        accessor: 'stores',
        title: 'Stores',
        width: 100,
        textAlign: 'left',
        render: ({ storesList, championStoreId }) => (
            <SurveyZoneStoreCount
                stores={storesList}
                championStoreId={championStoreId}
            />
        ),
    },
    {
        accessor: 'dueDate',
        title: 'Due Date',
        width: 110,
        textAlign: 'left',
        render: ({ dueDate, status }) => (
            <DueDateCell dueDate={dueDate} status={status} />
        ),
    },
    getStatusColumn(columnFilters, 180, 'blue', true),
    {
        accessor: 'progress',
        title: 'Progress',
        width: 250,
        textAlign: 'left',
        render: ({ progress = 0, progressText = 'Not Started' }) => {
            const {
                clampedValue,
                color: progressColor,
                textColor,
            } = getProgressColor(progress);

            return (
                <Group gap={12} align="center" w="100%">
                    <Progress
                        value={clampedValue}
                        size="md"
                        color={progressColor}
                        className="flex-1"
                    />
                    <Text
                        size="sm"
                        c={textColor}
                        className="font-medium min-w-[110px]"
                    >
                        {progressText}
                    </Text>
                </Group>
            );
        },
    },
    {
        accessor: 'lastActivity',
        title: 'Last Activity',
        width: 180,
        textAlign: 'left',
        render: ({ lastActivity }) => {
            const displayText = lastActivity || 'No recent activity';

            return (
                <Group gap={8} align="center">
                    <IconClock size={16} className="text-gray-400" />
                    <Text size="sm">{displayText}</Text>
                </Group>
            );
        },
    },
    getActionsColumn(
        isStoreManager,
        isDivisionalManager,
        undefined,
        onCancelSurvey,
    ),
];

export const getSurveyHistoryColumns = (
    isStoreManager: boolean,
    isDivisionalManager: boolean,
    columnFilters: SurveyTableColumnFilters,
    onEyeClick?: (surveyId: string) => void,
): DataTableColumn<SurveyRow>[] => [
    getAdZoneColumn(columnFilters),
    getSurveyListColumn(columnFilters),
    {
        accessor: 'stores',
        title: 'Stores',
        width: 100,
        textAlign: 'left',
        render: ({ storesList, championStoreId }) => (
            <SurveyZoneStoreCount
                stores={storesList}
                championStoreId={championStoreId}
            />
        ),
    },
    {
        accessor: 'dueDate',
        title: 'Due Date',
        width: 110,
        textAlign: 'left',
        render: ({ dueDate, status }) => (
            <DueDateCell dueDate={dueDate} status={status} />
        ),
    },
    {
        accessor: 'surveyPeriod',
        title: 'Survey Period',
        width: 200,
        textAlign: 'left',
    },
    getStatusColumn(columnFilters, 150, 'green'),
    {
        accessor: 'priceChange',
        title: 'Price Change',
        width: 150,
        textAlign: 'left',
        render: ({ priceChangeRequestsCount }) => (
            <Text size="sm">{priceChangeRequestsCount ?? 0} items</Text>
        ),
    },
    getActionsColumn(isStoreManager, isDivisionalManager, onEyeClick),
];

export type { CompetitorDrawerData };

export interface SurveyItemsColumnsOptions {
    showDMColumns?: boolean;
    showProposedSelling?: boolean;
    showDMReviewColumns?: boolean;
    surveyCompetitors?: PriceSurveyCompetitor[] | null;
    isStoreManager?: boolean;
    priceHandlers?: SurveyPriceHandlers;
    onCompetitorClick?: (data: CompetitorDrawerData) => void;
    onRemoveCompetitor?: (priceSurveyCompetitorId: string) => void;
    competitorCellsStatic?: boolean;
    advertisingZoneStores?: Store[];
    surveyId?: string;
    surveyCompleted?: boolean;
    productSalesData?: {
        thisYear: Record<string, number>;
        lastYear: Record<string, number>;
    };
    productSalesLoading?: boolean;
    productSalesWarmingIds?: Set<string>;
    productSalesWarmTimedOut?: boolean;
    salesMaintenanceProductIds?: Set<string>;
    salesMaintenanceCopy?: string;
    salesMaintenanceEndsAtLabel?: string;
    competitorsCollapsed?: boolean;
    onToggleCompetitorsCollapsed?: () => void;
    categoryCollapsed?: boolean;
    onToggleCategoryCollapsed?: () => void;
}

const CATEGORY_COLUMN_WIDTH = 150;
const CATEGORY_COLLAPSED_PLACEHOLDER_WIDTH = 52;
const SKU_COLUMN_WIDTH = 80;
const PCR_IMPLEMENTED_COLUMN_WIDTH = 160;

const COMPETITOR_COLUMN_WIDTH = 180;
const MARGIN_COLUMN_WIDTH = 120;
function SurveySalesMaintenanceCell({
    message,
    label,
}: {
    message: string;
    label: string;
}) {
    return (
        <Tooltip
            withArrow
            multiline
            w={280}
            classNames={{
                tooltip: 'bg-white p-0 shadow-lg',
            }}
            label={
                <Alert color="orange" title="Sales data maintenance">
                    {message}
                </Alert>
            }
        >
            <Group gap={6} wrap="nowrap" className="cursor-default">
                <IconInfoCircle size={14} className="text-orange-500" />
                <Text size="xs" c="orange">
                    {label}
                </Text>
            </Group>
        </Tooltip>
    );
}

function SurveyCurrentPriceCell({
    amount,
    currency,
}: {
    amount: number | null | undefined;
    currency: string;
}) {
    if (amount == null) {
        return (
            <Text size="sm" c="dimmed">
                —
            </Text>
        );
    }
    return (
        <Text size="sm" c="#E59332" fw={600}>
            {formatCurrency(amount, currency)}
        </Text>
    );
}

function getSurveyCurrentPriceColumns(
    showCashbuildPrice: boolean,
    showPnlPrice: boolean,
): DataTableColumn<SurveyItemRow>[] {
    const columns: DataTableColumn<SurveyItemRow>[] = [];
    if (showCashbuildPrice) {
        columns.push({
            accessor: 'currentCashbuildPrice',
            title: 'Cashbuild Price',
            width: 130,
            textAlign: 'left',
            titleClassName: '!min-w-[130px] !max-w-[130px]',
            cellsClassName: '!min-w-[130px] !max-w-[130px]',
            render: (row: SurveyItemRow) => (
                <SurveyCurrentPriceCell
                    amount={
                        row.currentCashbuildPrice ??
                        (showPnlPrice ? null : row.currentPrice)
                    }
                    currency={row.currency ?? 'ZAR'}
                />
            ),
        });
    }
    if (showPnlPrice) {
        columns.push({
            accessor: 'currentPnlPrice',
            title: 'P&L Price',
            width: 115,
            textAlign: 'left',
            titleClassName: '!min-w-[115px] !max-w-[115px]',
            cellsClassName: '!min-w-[115px] !max-w-[115px]',
            render: (row: SurveyItemRow) => (
                <SurveyCurrentPriceCell
                    amount={row.currentPnlPrice}
                    currency={row.currency ?? 'ZAR'}
                />
            ),
        });
    }
    return columns;
}

function getCompetitorColumnWidth(): number {
    return COMPETITOR_COLUMN_WIDTH;
}

const SKU_STICKY_TITLE_CLASS =
    '!sticky !left-0 !bg-[#f8fafc] !z-[20] !min-w-[80px] !max-w-[80px] shadow-right';
const SKU_STICKY_CELLS_CLASS =
    '!sticky !left-0 !bg-white !z-[5] !min-w-[80px] !max-w-[80px] shadow-right border-r border-gray-300 [tr[data-selected="true"]_&]:!bg-[#eff6ff]';

export const getSurveyItemsColumns = (
    options: SurveyItemsColumnsOptions = {},
): DataTableColumn<SurveyItemRow>[] => {
    const {
        showDMColumns = false,
        showProposedSelling = false,
        showDMReviewColumns = false,
        surveyCompetitors,
        isStoreManager = false,
        priceHandlers,
        onCompetitorClick,
        onRemoveCompetitor,
        competitorCellsStatic = false,
        advertisingZoneStores = [],
        surveyCompleted = false,
        productSalesData,
        productSalesLoading = false,
        productSalesWarmingIds,
        productSalesWarmTimedOut = false,
        salesMaintenanceProductIds,
        salesMaintenanceCopy = '',
        salesMaintenanceEndsAtLabel = '',
        competitorsCollapsed = false,
        onToggleCompetitorsCollapsed,
        categoryCollapsed = false,
        onToggleCategoryCollapsed,
    } = options;

    const isProductSalesPending = (productId?: string | null) => {
        if (productSalesWarmTimedOut) {
            return false;
        }
        if (productSalesLoading) {
            return true;
        }
        if (!productId) {
            return false;
        }
        return productSalesWarmingIds?.has(productId) ?? false;
    };

    const renderSalesMaintenanceCell = (productId?: string | null) => {
        if (
            !productId ||
            !salesMaintenanceCopy ||
            !salesMaintenanceProductIds?.has(productId)
        ) {
            return null;
        }
        return (
            <SurveySalesMaintenanceCell
                message={salesMaintenanceCopy}
                label={
                    salesMaintenanceEndsAtLabel
                        ? `Back at ${salesMaintenanceEndsAtLabel}`
                        : 'Under maintenance'
                }
            />
        );
    };

    const showCashbuildPrice =
        advertisingZoneStores.length === 0 ||
        shouldShowCashbuildPriceInput(advertisingZoneStores);
    const showPnlPrice =
        advertisingZoneStores.length > 0 &&
        shouldShowPnlPriceInput(advertisingZoneStores);
    const currentPriceColumns = getSurveyCurrentPriceColumns(
        showCashbuildPrice,
        showPnlPrice,
    );

    const isCompetitorsCollapsed =
        competitorsCollapsed && !!onToggleCompetitorsCollapsed;

    const isCategoryCollapsed =
        categoryCollapsed && !!onToggleCategoryCollapsed;

    const pcrImplementedColumn: DataTableColumn<SurveyItemRow> = {
        accessor: 'pcrImplemented',
        title: 'PCR Implemented',
        width: PCR_IMPLEMENTED_COLUMN_WIDTH,
        textAlign: 'left',
        titleClassName: '!min-w-[160px] !max-w-[160px]',
        cellsClassName: '!min-w-[160px] !max-w-[160px]',
        render: (row: SurveyItemRow) => {
            const pcr = row.priceChangeRequest;
            if (!pcr) {
                return (
                    <Text size="sm" c="dark.6">
                        —
                    </Text>
                );
            }

            const { latestStatus } = pcr;

            if (latestStatus === 'Declined') {
                return (
                    <Text size="sm" c="red.6">
                        Declined
                    </Text>
                );
            }

            if (
                latestStatus === 'Approved' ||
                latestStatus === 'ApprovedWithTemporaryPrice'
            ) {
                const implementedAt = pcr.completedAt ?? pcr.createdAt;
                return (
                    <Text size="sm" c="dark.6">
                        {dayjs(implementedAt).format('YYYY-MM-DD')}
                    </Text>
                );
            }

            return (
                <Text size="sm" c="orange.6">
                    Pending
                </Text>
            );
        },
    };

    const categoryColumn: DataTableColumn<SurveyItemRow> = {
        accessor: 'category',
        title: onToggleCategoryCollapsed ? (
            <Group
                gap={4}
                wrap="nowrap"
                justify="space-between"
                className="w-full min-w-0"
            >
                <span>Category</span>
                <PropagationStopper>
                    <Tooltip
                        label="Hide category column"
                        withArrow
                        position="bottom"
                        openDelay={300}
                    >
                        <UnstyledButton
                            type="button"
                            onClick={onToggleCategoryCollapsed}
                            aria-label="Hide category column"
                            className="shrink-0 flex items-center justify-center leading-none p-0"
                        >
                            <IconEyeOff
                                size={16}
                                stroke={2}
                                className="text-gray-600"
                            />
                        </UnstyledButton>
                    </Tooltip>
                </PropagationStopper>
            </Group>
        ) : (
            'Category'
        ),
        width: CATEGORY_COLUMN_WIDTH,
        textAlign: 'left',
        titleClassName: '!min-w-[150px] !max-w-[150px]',
        cellsClassName: '!min-w-[150px] !max-w-[150px]',
        render: ({ category }) => (
            <Text size="sm" c="dark.6">
                {category}
            </Text>
        ),
    };

    const categorySection: DataTableColumn<SurveyItemRow>[] =
        isCategoryCollapsed
            ? [
                  {
                      accessor: 'categoryCollapsedPlaceholder',
                      width: CATEGORY_COLLAPSED_PLACEHOLDER_WIDTH,
                      title: (
                          <PropagationStopper>
                              <Tooltip
                                  label="Show category column"
                                  withArrow
                                  position="bottom"
                                  openDelay={300}
                              >
                                  <UnstyledButton
                                      type="button"
                                      onClick={onToggleCategoryCollapsed}
                                      aria-label="Show category column"
                                      className="w-full flex items-center justify-center leading-none py-0.5 px-0"
                                  >
                                      <IconEye
                                          size={18}
                                          stroke={2}
                                          className="text-gray-600"
                                      />
                                  </UnstyledButton>
                              </Tooltip>
                          </PropagationStopper>
                      ),
                      titleClassName:
                          '!min-w-[52px] !max-w-[52px] border-r border-gray-200',
                      textAlign: 'center',
                      cellsClassName: '!min-w-[52px] !max-w-[52px]',
                      render: () => (
                          <Text size="sm" c="dimmed">
                              —
                          </Text>
                      ),
                  },
              ]
            : [categoryColumn];

    const baseColumns: DataTableColumn<SurveyItemRow>[] = [
        {
            accessor: 'rank',
            title: 'Rank',
            width: 80,
            textAlign: 'center',
            titleClassName: '!min-w-[80px] !max-w-[80px]',
            cellsClassName: '!min-w-[80px] !max-w-[80px]',
            render: ({ rank }) => (
                <div className="flex items-center justify-center">
                    <Badge
                        size="sm"
                        variant="filled"
                        radius="xl"
                        className="!bg-[#1458EC] !text-white font-semibold flex items-center justify-center leading-none !min-w-6 !h-6 !px-1.5 !text-xs shrink-0"
                    >
                        {rank}
                    </Badge>
                </div>
            ),
        },
        {
            accessor: 'sku',
            title: 'SKU',
            width: SKU_COLUMN_WIDTH,
            textAlign: 'left',
            titleClassName: SKU_STICKY_TITLE_CLASS,
            cellsClassName: SKU_STICKY_CELLS_CLASS,
            render: ({ sku }) => {
                const formattedSku = sku.replace(/^0+(?=\d)/, '');
                return (
                    <Text size="sm" className="text-primary" fw={500}>
                        #{formattedSku}
                    </Text>
                );
            },
        },
        ...categorySection,
        {
            accessor: 'description',
            title: 'Description',
            width: 300,
            textAlign: 'left',
            titleClassName: '!min-w-[300px] !max-w-[300px]',
            cellsClassName: '!min-w-[300px] !max-w-[300px]',
            ellipsis: true,
            render: ({ description }) => (
                <Text size="sm" c="dark.5" lineClamp={1}>
                    {description}
                </Text>
            ),
        },
    ];

    if (showDMColumns && surveyCompleted) {
        baseColumns.push(pcrImplementedColumn);
    }

    if (!showDMColumns) {
        if (!surveyCompleted) {
            baseColumns.push({
                accessor: 'status',
                title: 'Status',
                width: 80,
                textAlign: 'center',
                titleClassName: '!min-w-[80px] !max-w-[80px]',
                cellsClassName: '!min-w-[80px] !max-w-[80px]',
                render: ({ isComplete }) => (
                    <div className="flex justify-center">
                        {isComplete ? (
                            <ThemeIcon
                                size="sm"
                                radius="xl"
                                variant="transparent"
                                className="!bg-green-500/20 !text-green-500"
                            >
                                <IconCheck size={14} stroke={2.5} />
                            </ThemeIcon>
                        ) : null}
                    </div>
                ),
            });
        }

        baseColumns.push(...currentPriceColumns);
    } else {
        baseColumns.push(
            ...currentPriceColumns,
            {
                accessor: 'costPrice',
                title: 'Cost Price',
                width: 115,
                textAlign: 'left',
                titleClassName: '!min-w-[115px] !max-w-[115px]',
                cellsClassName: '!min-w-[115px] !max-w-[115px]',
                render: (row: SurveyItemRow) =>
                    row.currentCostPrice == null ? (
                        <Text size="sm" c="dimmed">
                            —
                        </Text>
                    ) : (
                        <Text size="sm" c="dark.5" fw={500}>
                            {formatCurrency(
                                row.currentCostPrice,
                                row.currency ?? 'ZAR',
                            )}
                        </Text>
                    ),
            },
            {
                accessor: 'margins',
                title: 'Margins',
                width: MARGIN_COLUMN_WIDTH,
                textAlign: 'left',
                titleClassName: '!min-w-[120px] !max-w-[120px]',
                cellsClassName: '!min-w-[120px] !max-w-[120px]',
                render: (row: SurveyItemRow) => (
                    <SurveyMarginTableCell
                        row={row}
                        showCashbuildPrice={showCashbuildPrice}
                        showPnlPrice={showPnlPrice}
                    />
                ),
            },
        );
    }

    const competitorColumns: DataTableColumn<SurveyItemRow>[] = (
        surveyCompetitors || []
    ).map((comp, index) => {
        const isFirst = index === 0;

        const competitorEdgeClasses = `competitor-col-edge${
            isFirst ? ' competitor-col-edge--first' : ''
        }`;

        const showCompetitorAttachmentButton = !!onCompetitorClick;
        const showRemoveCompetitorButton =
            isStoreManager && !!onRemoveCompetitor && !competitorCellsStatic;
        const isCompetitorCellEditable =
            isStoreManager && !!priceHandlers && !competitorCellsStatic;
        return {
            accessor: `competitor_${comp.competitorId}`,
            width: getCompetitorColumnWidth(),
            title: (
                <CompetitorColumnHeader
                    comp={comp}
                    onCompetitorClick={onCompetitorClick}
                    onRemoveFromSurvey={onRemoveCompetitor}
                    showAttachmentButton={showCompetitorAttachmentButton}
                    showRemoveButton={showRemoveCompetitorButton}
                    showCollapseSectionButton={
                        isFirst && !!onToggleCompetitorsCollapsed
                    }
                    onToggleCompetitorsSection={onToggleCompetitorsCollapsed}
                />
            ),
            titleClassName: `bg-[#85939F] !min-w-[180px] !max-w-[180px] text-white ${competitorEdgeClasses}`,
            textAlign: 'left',
            cellsClassName: `competitor-cell-fill ${competitorEdgeClasses} !min-w-[180px] !max-w-[180px]`,
            render: (row: SurveyItemRow) => {
                const priceData = row.competitorPrices[comp.competitorId];
                return (
                    <CompetitorPriceCell
                        priceId={priceData?.priceId}
                        price={priceData?.price ?? null}
                        isNA={priceData?.isNA ?? false}
                        currency={row.currency ?? 'ZAR'}
                        isEditable={isCompetitorCellEditable}
                        isSaving={priceHandlers?.isSaving ?? false}
                        updateCell={priceHandlers?.updateCell}
                    />
                );
            },
        };
    });

    let competitorSection: DataTableColumn<SurveyItemRow>[];
    if (isCompetitorsCollapsed) {
        competitorSection = [
            {
                accessor: 'competitorsCollapsedPlaceholder',
                width: 52,
                title: (
                    <PropagationStopper>
                        <Tooltip
                            label="Show competitor columns"
                            withArrow
                            position="bottom"
                            openDelay={300}
                        >
                            <UnstyledButton
                                type="button"
                                onClick={onToggleCompetitorsCollapsed}
                                aria-label="Show competitor columns"
                                className="w-full flex items-center justify-center leading-none py-0.5 px-0"
                            >
                                <IconEye
                                    size={18}
                                    stroke={2}
                                    className="text-white"
                                />
                            </UnstyledButton>
                        </Tooltip>
                    </PropagationStopper>
                ),
                titleClassName:
                    'bg-[#85939F] !min-w-[52px] !max-w-[52px] text-white competitor-col-edge competitor-col-edge--first',
                textAlign: 'center',
                cellsClassName:
                    'competitor-cell-fill competitor-col-edge competitor-col-edge--first !min-w-[52px] !max-w-[52px]',
                render: () => (
                    <Text size="sm" c="dimmed">
                        —
                    </Text>
                ),
            },
        ];
    } else {
        competitorSection = competitorColumns;
    }

    if (!showDMColumns) {
        return [...baseColumns, ...competitorSection];
    }

    const dmReviewColumns: DataTableColumn<SurveyItemRow>[] = [
        {
            accessor: 'priceVariance',
            title: 'Price Variance',
            width: 120,
            textAlign: 'left',
            titleClassName: 'bg-[#EBF1F7] !min-w-[100px]',
            cellsClassName: '!min-w-[100px]',
            render: (row: SurveyItemRow) => <PriceVarianceCell row={row} />,
        },
        {
            accessor: 'unitSalesThisYear',
            title: (
                <Tooltip
                    label="Unit Sales vs last 4 financial weeks (this year)"
                    openDelay={300}
                    withArrow
                >
                    <span>Unit Sales TY</span>
                </Tooltip>
            ),
            width: 120,
            textAlign: 'left',
            titleClassName: 'bg-[#EBF1F7] !min-w-[120px]',
            cellsClassName: '!min-w-[120px]',
            render: (row: SurveyItemRow) => {
                const maintenanceCell = renderSalesMaintenanceCell(
                    row.productId,
                );
                if (maintenanceCell) {
                    return maintenanceCell;
                }
                const { productId } = row;
                if (isProductSalesPending(productId)) {
                    return <Loader size="xs" />;
                }
                if (!productId) {
                    return (
                        <Text size="sm" c="dark.6">
                            —
                        </Text>
                    );
                }
                const value = productSalesData?.thisYear[productId];
                return (
                    <Text size="sm" c="dark.6">
                        {value != null ? value.toLocaleString() : '—'}
                    </Text>
                );
            },
        },
        {
            accessor: 'unitSalesLastYear',
            title: (
                <Tooltip
                    label="Unit Sales vs last 4 financial weeks (last year)"
                    openDelay={300}
                    withArrow
                >
                    <span>Unit Sales LY</span>
                </Tooltip>
            ),
            width: 120,
            textAlign: 'left',
            titleClassName: 'bg-[#EBF1F7] !min-w-[120px]',
            cellsClassName: '!min-w-[120px]',
            render: (row: SurveyItemRow) => {
                const maintenanceCell = renderSalesMaintenanceCell(
                    row.productId,
                );
                if (maintenanceCell) {
                    return maintenanceCell;
                }
                const { productId } = row;
                if (isProductSalesPending(productId)) {
                    return <Loader size="xs" />;
                }
                if (!productId) {
                    return (
                        <Text size="sm" c="dark.6">
                            —
                        </Text>
                    );
                }
                const value = productSalesData?.lastYear[productId];
                return (
                    <Text size="sm" c="dark.6">
                        {value != null ? value.toLocaleString() : '—'}
                    </Text>
                );
            },
        },
        {
            accessor: 'qtyVariance',
            title: (
                <Tooltip
                    label="Qty Variance (This Year − Last Year)"
                    openDelay={300}
                    withArrow
                >
                    <span>Qty Variance</span>
                </Tooltip>
            ),
            width: 120,
            textAlign: 'left',
            titleClassName: 'bg-[#EBF1F7] !min-w-[120px]',
            cellsClassName: '!min-w-[120px]',
            render: (row: SurveyItemRow) => {
                const maintenanceCell = renderSalesMaintenanceCell(
                    row.productId,
                );
                if (maintenanceCell) {
                    return maintenanceCell;
                }
                const { productId } = row;
                if (isProductSalesPending(productId)) {
                    return <Loader size="xs" />;
                }
                if (!productId) {
                    return (
                        <Text size="sm" c="dark.6">
                            —
                        </Text>
                    );
                }
                const thisYearQuantity = productSalesData?.thisYear[productId];
                const lastYearQuantity = productSalesData?.lastYear[productId];
                if (thisYearQuantity == null && lastYearQuantity == null) {
                    return (
                        <Text size="sm" c="dark.6">
                            —
                        </Text>
                    );
                }
                const variance =
                    (thisYearQuantity ?? 0) - (lastYearQuantity ?? 0);
                let varianceColor: string = 'dark.6';
                if (variance > 0) {
                    varianceColor = 'green.7';
                } else if (variance < 0) {
                    varianceColor = 'red.7';
                }
                return (
                    <Text
                        size="sm"
                        c={varianceColor}
                        fw={variance !== 0 ? 600 : undefined}
                    >
                        {variance > 0 ? '+' : ''}
                        {variance.toLocaleString()}
                    </Text>
                );
            },
        },
    ];

    const pcrSubmittedColumn: DataTableColumn<SurveyItemRow> = {
        accessor: 'pcrSubmitted',
        title: 'PCR Submitted',
        width: 120,
        textAlign: 'left',
        titleClassName: 'bg-[#EBF1F7] !min-w-[120px]',
        cellsClassName: '!min-w-[120px]',
        render: (row: SurveyItemRow) => {
            const pcr = row.priceChangeRequest;
            if (!pcr) {
                return (
                    <Text size="sm" c="dark.6">
                        —
                    </Text>
                );
            }
            return (
                <Text size="sm" c="dark.6">
                    {dayjs(pcr.createdAt).format('YYYY-MM-DD')}
                </Text>
            );
        },
    };

    const pcrPriceColumn: DataTableColumn<SurveyItemRow> = {
        accessor: 'pcrPrice',
        title: 'PCR Price',
        width: 100,
        textAlign: 'left',
        titleClassName: 'bg-[#EBF1F7] !min-w-[100px]',
        cellsClassName: '!min-w-[100px]',
        render: (row: SurveyItemRow) => {
            const pcr = row.priceChangeRequest;
            if (!pcr) {
                return (
                    <Text size="sm" c="dark.6">
                        —
                    </Text>
                );
            }
            return (
                <Text size="sm" c="dark.6">
                    {formatCurrency(pcr.proposedPrice, row.currency ?? 'ZAR')}
                </Text>
            );
        },
    };

    const reviewColumns = showDMReviewColumns ? dmReviewColumns : [];
    const completedOnlyColumns = surveyCompleted
        ? [pcrSubmittedColumn, pcrPriceColumn]
        : [];

    if (showProposedSelling) {
        const proposedSellingColumn: DataTableColumn<SurveyItemRow> = {
            accessor: 'proposedSelling',
            title: <ProposedSellingColumnHeader />,
            width: 250,
            textAlign: 'left',
            titleClassName:
                '!bg-[#46515B] !min-w-[250px] !max-w-[250px] !text-white',
            cellsClassName:
                '!min-w-[250px] !max-w-[250px] [tr[data-selected="true"]_&]:!bg-[#eff6ff] !p-2',
            render: (row: SurveyItemRow) => (
                <ProposedSellingCellWrapper row={row} />
            ),
        };
        return [
            ...baseColumns,
            ...competitorSection,
            ...reviewColumns,
            ...completedOnlyColumns,
            proposedSellingColumn,
        ];
    }

    return [
        ...baseColumns,
        ...competitorSection,
        ...reviewColumns,
        ...completedOnlyColumns,
    ];
};
